import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

import { AuthProvider } from "./lib/auth-context";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import FollowUpBooking from "./pages/FollowUpBooking";
import { AdminBanner } from "./components/admin/AdminBanner";
import { Tags } from "./components/Tags";
import { CookieConsent } from "./components/CookieConsent";
import CookiePolicy from "./pages/CookiePolicy";
import Complaints from "./pages/Complaints";
import { captureVisitSource } from "./lib/visitSource";

import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import ServiceDetail from "./pages/ServiceDetail";
import Consultants from "./pages/Consultants";
import ConsultantDetail from "./pages/ConsultantDetail";
import PatientInfo from "./pages/PatientInfo";
import Referrals from "./pages/Referrals";
import Research from "./pages/Research";
import Pricing from "./pages/Pricing";
import Contact from "./pages/Contact";
import Blog from "./pages/Blog";
import Enquiry from "./pages/BookAppointment";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import CompletePayment from "./pages/CompletePayment";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import Dashboard from "./pages/Dashboard";
import AdminEnquiries from "./pages/AdminEnquiries";
import ArabicPatients from "./pages/ArabicPatients";
import VideoDownloads from "./pages/VideoDownloads";
import AdminPayments from "./pages/AdminPayments";
import AdminTeam from "./pages/AdminTeam";
import AdminReturns from "./pages/AdminReturns";
import PayRequest from "./pages/PayRequest";
import { ChatWidget } from "./components/assistant/ChatWidget";
import AdminPatients from "./pages/AdminPatients";
import AdminBookings from "./pages/AdminBookings";
import AdminAvailability from "./pages/AdminAvailability";
import AdminCalendar from "./pages/AdminCalendar";
import ManageBooking from "./pages/ManageBooking";
import ResetPassword from "./pages/ResetPassword";
import MfaSetup from "./pages/MfaSetup";
import Privacy from "./pages/Privacy";
import ScrollToTop from "./components/ScrollToTop";
import Terms from "./pages/Terms";

captureVisitSource();

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AdminBanner />
          <Tags />
          <CookieConsent />
          <ChatWidget />
          <Toaster richColors position="top-right" />
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/consultants" element={<Consultants />} />
              <Route path="/consultants/:slug" element={<ConsultantDetail />} />
              <Route path="/patient-info" element={<PatientInfo />} />
              <Route path="/referrals" element={<Referrals />} />
              <Route path="/research" element={<Research />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/blog" element={<Blog />} />
              {/* Legacy hand-authored blog post URLs redirect to the new news-driven Journal */}
              <Route path="/blog/:slug" element={<Navigate to="/blog" replace />} />
              <Route path="/enquiry" element={<Enquiry />} />
              <Route path="/follow-up" element={<FollowUpBooking />} />
              <Route path="/complete-payment" element={<CompletePayment />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/mfa-setup" element={<ProtectedRoute><MfaSetup /></ProtectedRoute>} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/complaints" element={<Complaints />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/admin/enquiries" element={<ProtectedRoute role="admin"><AdminEnquiries /></ProtectedRoute>} />
              <Route path="/ar" element={<ArabicPatients />} />
              <Route path="/video-downloads" element={<VideoDownloads />} />
              <Route path="/admin/payments" element={<ProtectedRoute role="admin"><AdminPayments /></ProtectedRoute>} />
              <Route path="/admin/team" element={<ProtectedRoute role="admin"><AdminTeam /></ProtectedRoute>} />
              <Route path="/admin/returns" element={<ProtectedRoute role="admin"><AdminReturns /></ProtectedRoute>} />
              <Route path="/pay/:token" element={<PayRequest />} />
              <Route path="/admin/patients" element={<ProtectedRoute role="admin"><AdminPatients /></ProtectedRoute>} />
              <Route path="/admin/bookings" element={<ProtectedRoute role="admin"><AdminBookings /></ProtectedRoute>} />
              <Route path="/admin/availability" element={<ProtectedRoute role="admin"><AdminAvailability /></ProtectedRoute>} />
              <Route path="/admin/calendar" element={<ProtectedRoute role="admin"><AdminCalendar /></ProtectedRoute>} />
              <Route path="/manage-booking" element={<ManageBooking />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
